import { addDoc, arrayUnion, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';

/** Discriminator for merged notification inbox rows. */
export const INBOX_KIND_ANNOUNCEMENT = 'announcement';
export const INBOX_KIND_ALERT = 'alert';

/** Roles a company admin can target with a broadcast. */
export const ANNOUNCEMENT_TARGET_ROLES = ['developer', 'manager', 'agent'];

export const ANNOUNCEMENT_ROLE_LABELS = {
  developer: 'Developer',
  manager: 'Manager',
  agent: 'Agent',
};

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {{
 *   companyId: string,
 *   title: string,
 *   message: string,
 *   targetRoles: string[],
 *   createdBy: string,
 * }} payload
 */
export async function createAnnouncement(db, payload) {
  const { companyId, title, message, targetRoles, createdBy } = payload;
  if (!companyId || !createdBy) {
    throw new Error('Missing company or author.');
  }
  const trimmedTitle = title.trim();
  const trimmedMessage = message.trim();
  if (!trimmedTitle) throw new Error('Title is required.');
  if (!trimmedMessage) throw new Error('Message is required.');
  if (!Array.isArray(targetRoles) || targetRoles.length === 0) {
    throw new Error('Select at least one target role.');
  }

  const roles = targetRoles.filter((r) => ANNOUNCEMENT_TARGET_ROLES.includes(r));
  if (roles.length === 0) {
    throw new Error('Select at least one valid target role.');
  }

  return addDoc(collection(db, 'announcements'), {
    companyId,
    title: trimmedTitle,
    message: trimmedMessage,
    createdBy,
    targetRoles: roles,
    timestamp: serverTimestamp(),
    seenBy: [],
  });
}

/** @param {import('firebase/firestore').Timestamp | Date | null | undefined} ts */
export function formatAnnouncementDate(ts) {
  if (!ts) return '—';
  const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

/** @param {import('firebase/firestore').Timestamp | Date | null | undefined} ts */
export function parseAnnouncementTimestamp(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** @param {{ seenBy?: string[] } | null | undefined} announcement @param {string | null | undefined} uid */
export function isAnnouncementSeen(announcement, uid) {
  if (!uid || !announcement) return false;
  return Array.isArray(announcement.seenBy) && announcement.seenBy.includes(uid);
}

/**
 * Mark an announcement as seen by the current user.
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} announcementId
 * @param {string} uid
 */
export async function markAnnouncementAsSeen(db, announcementId, uid) {
  if (!announcementId || !uid) return;
  await updateDoc(doc(db, 'announcements', announcementId), {
    seenBy: arrayUnion(uid),
  });
}

/** @param {{ inboxKind?: string } | null | undefined} item */
export function isInboxItemAnnouncement(item) {
  return item?.inboxKind === INBOX_KIND_ANNOUNCEMENT;
}

/**
 * Unified read state for merged inbox rows (alerts + announcements).
 * @param {{ inboxKind?: string, seenBy?: string[], readBy?: Record<string, unknown> } | null | undefined} item
 * @param {string | null | undefined} uid
 */
export function isInboxItemRead(item, uid) {
  if (!item || !uid) return false;
  if (isInboxItemAnnouncement(item)) return isAnnouncementSeen(item, uid);
  return Boolean(item.readBy && item.readBy[uid]);
}

/**
 * Normalize a Firestore announcement doc for the shared notifications inbox.
 * @param {{ id: string } & Record<string, unknown>} announcement
 */
export function normalizeAnnouncementForInbox(announcement) {
  const ts = announcement.timestamp ?? announcement.createdAt ?? null;
  return {
    ...announcement,
    inboxKind: INBOX_KIND_ANNOUNCEMENT,
    title: announcement.title || 'Announcement',
    message: announcement.message || '',
    severity: 'info',
    createdAt: ts,
    createdByEmail: 'Company Admin',
  };
}

/**
 * @param {{ inboxKind?: string, createdAt?: unknown, timestamp?: unknown }} item
 */
export function inboxItemSortTime(item) {
  const raw = isInboxItemAnnouncement(item) ? (item.timestamp ?? item.createdAt) : item.createdAt;
  return parseAnnouncementTimestamp(raw)?.getTime() || 0;
}
